export const BROKERS = ["apollo", "pepperstone", "avatrade", "etoro"] as const;
export type Broker = (typeof BROKERS)[number];

export type DateRange = "7d" | "30d";

export interface BrokerMetricsBase {
  broker: Broker;
  spend: number;
  regs: number;
  cpr: number | null;
}

export interface ApolloMetrics extends BrokerMetricsBase {
  broker: "apollo";
  commissions: number;
}

export interface PepperstoneMetrics extends BrokerMetricsBase {
  broker: "pepperstone";
  kycStarted: number;
  kycCompleted: number;
  startedTrading: number;
}

export interface AvatradeMetrics extends BrokerMetricsBase {
  broker: "avatrade";
}

export interface EtoroMetrics extends BrokerMetricsBase {
  broker: "etoro";
}

export type BrokerMetrics =
  | ApolloMetrics
  | PepperstoneMetrics
  | AvatradeMetrics
  | EtoroMetrics;

export interface OverviewPayload {
  generatedAt: string;
  range: DateRange;
  brokers: BrokerMetrics[];
}

export function cpr(spend: number, regs: number): number | null {
  return regs > 0 ? spend / regs : null;
}

export interface CampaignRow {
  broker: Broker;
  campaign: string;
  date: string;
  spend: number;
  regs: number;
}

export const DATE_PRESETS = [
  "today",
  "yesterday",
  "last7",
  "last30",
  "mtd",
  "ltd",
] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

export const DATE_PRESET_LABEL: Record<DatePreset, string> = {
  today: "Heute",
  yesterday: "Gestern",
  last7: "Letzte 7 Tage",
  last30: "Letzte 30 Tage",
  mtd: "Diesen Monat",
  ltd: "Letzten Monat",
};

/** Resolve a preset to explicit from/to YYYY-MM-DD strings (inclusive). */
export function resolveDatePreset(preset: DatePreset, now: Date = new Date()): { from: string; to: string } {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const fmt = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  switch (preset) {
    case "today":
      return { from: fmt(today), to: fmt(today) };
    case "yesterday": {
      const y = new Date(today.getTime() - 86400_000);
      return { from: fmt(y), to: fmt(y) };
    }
    case "last7": {
      const from = new Date(today.getTime() - 6 * 86400_000);
      return { from: fmt(from), to: fmt(today) };
    }
    case "last30": {
      const from = new Date(today.getTime() - 29 * 86400_000);
      return { from: fmt(from), to: fmt(today) };
    }
    case "mtd": {
      const from = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
      return { from: fmt(from), to: fmt(today) };
    }
    case "ltd": {
      const from = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
      const to = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0));
      return { from: fmt(from), to: fmt(to) };
    }
  }
}

export type MsAdsCampaignStatus = "Active" | "Paused" | "BudgetPaused" | "Deleted" | "Suspended" | string;

/** Aggregated per-campaign row as shown in the MS Ads UI. */
export interface MsAdsCampaign {
  accountId: string;
  accountName: string;
  campaignId: string;
  campaignName: string;
  campaignType: string;
  status: MsAdsCampaignStatus;
  dailyBudget: number | null;
  budgetType: string | null;
  broker: Broker | null;
  impressions: number;
  clicks: number;
  ctr: number | null;
  averageCpc: number | null;
  spend: number;
  conversions: number;
  conversionRate: number | null;
  costPerConversion: number | null;
  /** Echte MS-Attribution der Tracker-Offline-Goals je Kampagne (MS bucht auf
   *  Klick-Datum). Optional: fehlt bei Google-Ads-Rows und Alt-Cache-Payloads. */
  goalSignups?: number;
  goalFtd?: number;
  goalTrader?: number;
}

export interface MsAdsCampaignsPayload {
  from: string;
  to: string;
  generatedAt: string;
  campaigns: MsAdsCampaign[];
}

/** Anzeigengruppe einer Microsoft-Kampagne (Operator 2026-10-20: eToro-FTD-Kampagne, Laender = Anzeigengruppen).
 *  goal* = MS-Attribution der Tracker-Offline-Ziele je Gruppe (Klick-Datum), wie bei MsAdsCampaign. */
export interface MsAdsAdGroup {
  adGroupId: string;
  adGroupName: string;
  status: string;
  impressions: number;
  clicks: number;
  spend: number;
  goalSignups: number;
  goalFtd: number;
  goalTrader: number;
}

export interface MsAdsAdGroupsPayload {
  from: string;
  to: string;
  generatedAt: string;
  accountId: string;
  campaignId: string;
  adGroups: MsAdsAdGroup[];
}

/** Per-source registration/FTD count (Impact, PartnerStack, Tracker). */
export interface CompanyRegSummary {
  company: string;
  regs: number;
}

export interface CompanyRegsPayload {
  from: string;
  to: string;
  generatedAt: string;
  companies: CompanyRegSummary[];
  /** Per-source fetch errors; present only when a source failed (partial data). */
  errors?: string[];
}

export type EtoroStage = "registration" | "ftd" | "cpa";

/** eToro conversion counts split by country (geo) and ad source (google/microsoft). */
export interface EtoroBreakdownRow {
  stage: EtoroStage;
  geo: string;      // "fr", "es", … or "unknown"
  source: string;   // "google", "microsoft", … or "unknown"
  count: number;
}

export interface EtoroBreakdownPayload {
  from: string;
  to: string;
  breakdown: EtoroBreakdownRow[];
}

// ---- Websites tab (Northwebsites cold outreach — proxied from the VM dashboard) ----

/** Ein Postfach der Sendeflotte mit seinem heutigen Warm-up-Stand. */
export interface WebsitesFleetBox {
  address: string;
  done: number;
  cap: number;
  persona: "peter" | "luise" | string;
}

export interface WebsitesSummary {
  /** Summen über die GESAMTE Laufzeit — nicht über den gewählten Zeitraum.
   *  Der Zeitraum steckt in `tiles`/`funnel`/`series`. */
  sent: number;
  replies: number;
  bounces: number;
  previewsOpened: number;
  /** Nur von Scannern besuchte Previews — getrennt, nie dazugezählt. */
  previewsMaybeOpened?: number;
  activePreviews: number;
  gate: "LIVE" | "DRY-RUN" | "KILL";
  paused: string | null;
  fleet?: WebsitesFleetBox[];
}

/** Die sechs Funnelstufen. Zugleich die Reihenfolge der Kacheln. */
export type WebsitesStage =
  | "sent"
  | "delivered"
  | "demoViewed"
  | "replied"
  | "positive"
  | "sold";

export interface WebsitesTile {
  key: WebsitesStage;
  value: number;
  /** Anteil an den versendeten Mails der Kohorte; bei `sent` selbst null. */
  rate: number | null;
  /** Veränderung zur Vorperiode in Prozent — null, wenn es keine Bezugsgrösse gibt. */
  delta: number | null;
  previous: number;
}

export interface WebsitesFunnelStage {
  key: WebsitesStage;
  label: string;
  value: number;
  ofFirst: number | null;
  ofPrevious: number | null;
}

export interface WebsitesSeriesPoint {
  date: string;
  sent: number;
  demoViewed: number;
  replies: number;
}

export interface WebsitesNeedsReplyItem {
  convId: number;
  leadId: number | null;
  firma: string | null;
  from: string;
  subject: string;
  receivedAt: string;
  ageMinutes: number;
  outcome: WebsitesOutcome;
}

export interface WebsitesHealth {
  bounceRate: number | null;
  bounced: number;
  bounceTotal: number;
  /** Immer null: keine FBL angebunden. "Nicht erhoben", nicht "null". */
  spamComplaints: number | null;
  /** Google Postmaster je Send-Domain (seit 20.08.). Felder null = von Google noch
   *  nicht erhoben (Volumen-Schwelle / 1-2 Tage Verzug). */
  postmaster?:
    | { domain: string; reputation?: string | null; spamRatio?: number | null; day?: string | null }[]
    | null;
}

/** Urteil über eine eingehende Antwort. null = noch nicht beurteilt. */
export type WebsitesOutcome = "interested" | "neutral" | "declined" | null;

export type WebsitesCategory = "replied" | "preview_opened" | "contacted" | "queued" | "other";

export interface WebsitesPipelineRow {
  leadId: number;
  firma: string;
  /** Die Mail dieses Leads: versendete aus dem Archiv, geplante live komponiert.
   *  `reconstructed` = Altbestand ohne archivierten Volltext (Rekonstruktion). */
  mail?: {
    subject: string;
    body: string;
    sent?: boolean;
    reconstructed?: boolean;
    error?: string;
  } | null;
  previewUrl: string | null;
  previewVisits: number;
  previewFirstVisit: string | null;
  contactedAt: string | null;
  repliedAt: string | null;
  category: WebsitesCategory;
  status: string;
}

export interface WebsitesInboxItem {
  convId: number;
  /** Postfach, das die Mail empfangen hat — bestimmt, unter welchem Namen geantwortet wird. */
  mailbox?: string;
  outcome?: WebsitesOutcome;
  from: string;
  subject: string;
  body: string;
  receivedAt: string;
  leadId: number | null;
  firma: string | null;
  draft: { id: number; subject: string; body: string } | null;
  sentReply: { sentAt: string; body: string } | null;
}

export interface WebsitesRange {
  from: string;
  to: string;
  previous?: { from: string; to: string };
}

/** Planstand eines Sendetags. `planned: null` = kein Plan (nicht 0!). */
export interface WebsitesPlanDay {
  sendDay: string;
  planId?: string | null;
  planned: number | null;
  sent?: number | null;
  /** null = kein Vorflug-Ergebnis für den Tag. */
  preflightOk?: boolean | null;
  /** true = Tag vollständig eingefroren (day_ready), false = Teilfreeze, null = kein Freeze. */
  dayReady?: boolean | null;
}

/**
 * Die Kachel-Reihe über der Lead-Liste: der Trichter vom Scrape bis zum Sendetag.
 * Jede Zahl kann null sein — „nicht erhoben“, als Strich gezeigt (format.DASH).
 */
export interface WebsitesPipelineStats {
  scraped: number | null;
  /** Policy-Pool: wen wir überhaupt anschreiben dürften. */
  sendablePolicy: number | null;
  /** davon mit belegt zustellbarer Adresse — die Zahl, die die Sendegates überlebt. */
  sendable: number | null;
  /** davon mit gebauter Preview. */
  built: number | null;
  /** davon fertig: Peters Text, Konfiguration frisch. */
  ready: number | null;
  /** davon je von Peter gegengelesen (peter_lead_checked). */
  checked: number | null;
  today: WebsitesPlanDay | null;
  tomorrow: WebsitesPlanDay | null;
  /** Geplante Mails je Tag, nächste 7 Kalendertage. `frozen` = Tag bereits abgenommen. */
  forecast?: { sendDay: string; planned: number; frozen?: boolean }[] | null;
  /** Reichweite: verifizierter Pool ÷ mittlere Tagesnachfrage. null = nicht berechenbar. */
  runwayDays?: number | null;
}

export interface WebsitesPayload {
  generatedAt: string;
  range: WebsitesRange;
  summary: WebsitesSummary;
  /** Optional: ältere VM-Payloads tragen den Key noch nicht — die Reihe zeigt dann Striche. */
  pipelineStats?: WebsitesPipelineStats;
  tiles: WebsitesTile[];
  funnel: WebsitesFunnelStage[];
  series: WebsitesSeriesPoint[];
  needsReply: WebsitesNeedsReplyItem[];
  health: WebsitesHealth;
  pipeline: WebsitesPipelineRow[];
  inbox: WebsitesInboxItem[];
}

// ---- A/B-Test ----

export interface WebsitesCounts {
  sent: number;
  bounced: number;
  delivered: number;
  demoViewed: number;
  demoMaybe: number;
  replied: number;
  positive: number;
  declined: number;
  sold: number;
}

export interface WebsitesArmMetric {
  value: number;
  /** null, solange der Arm unter der Mindestgrösse liegt — bewusst KEINE Quote. */
  rate: number | null;
}

export type WebsitesArm = {
  key: string;
  label: string | null;
  readable: boolean;
  counts: WebsitesCounts;
} & Record<WebsitesStage, WebsitesArmMetric>;

export interface WebsitesAbCell {
  version: string;
  persona: string;
  counts: WebsitesCounts;
  readable: boolean;
}

export interface WebsitesAssignmentRow {
  version: string;
  persona: string;
  leads: number;
  inTest: boolean;
}

export interface WebsitesChatStats {
  sessions: number;
  inbound: number;
  outbound: number;
  escalations: number;
  escalationRate: number | null;
  messagesPerSession: number | null;
}

export interface WebsitesAbtestPayload {
  range: { from: string; to: string };
  minCell: number;
  /** Die Versionen, die aktuell gegeneinander antreten (config.yaml outreach.ab_arms). */
  arms: string[];
  byVersion: WebsitesArm[];
  byPersona: WebsitesArm[];
  cells: WebsitesAbCell[];
  comparedTotal: WebsitesCounts;
  outsideTotal: WebsitesCounts;
  outsideByVersion: WebsitesArm[];
  assignment: WebsitesAssignmentRow[];
  legacy: WebsitesCounts;
  luiseChat: WebsitesChatStats;
  note: string;
}

// ---- Kunden ----

export interface WebsitesCustomerRow {
  id: number;
  leadId: number | null;
  firma: string | null;
  email: string | null;
  domain: string | null;
  status: string;
  liveVersion: string | null;
  since: string;
  mailVersion: string | null;
  mrr: number;
}

export interface WebsitesCustomersPayload {
  range: { from: string; to: string };
  monthlyPriceUsd: number;
  byStatus: Record<string, number>;
  tiles: {
    active: number;
    mrr: number;
    arpu: number;
    newInRange: number;
    cancelledInRange: number;
    churnRate: number | null;
    churnBase: number;
  };
  payments: {
    paidCents: number;
    paidCount: number;
    refundedCents: number;
    refundedCount: number;
    failed: number;
    disputed: number;
    unmatched: number;
  };
  openEscalations: {
    id: number;
    leadId: number | null;
    type: string;
    detail: string;
    createdAt: string;
  }[];
  liveSites: number;
  customers: WebsitesCustomerRow[];
  months: { month: string; new: number; cancelled: number }[];
}

// ---- RoboForex partner tiles (Brokers tab) ----

export interface RoboforexPayload {
  from: string;
  to: string;
  generatedAt: string;
  /** ACCOUNTS whose reg_date falls inside [from, to] (one customer can open several) */
  registrations: number | null;
  /** new CUSTOMERS in range — diff of daily snapshots of the cumulative customer
   *  counter; null when the range has no baseline (counting started 2026-08-08) */
  newCustomers: number | null;
  /** cumulative "deposited clients" — the API has no per-day FTD event (operator decision) */
  depositedClients: number | null;
  /** commission USD summed over the range's per-day reports */
  commission: number | null;
  /** days of the range whose commission is included (KV backfills progressively) */
  commissionDaysCovered: number;
  /** Lots traded in the range by the accounts OUR clicks brought (tracker attribution);
   *  null when the tracker could not be reached — never silently 0. */
  volumeLots: number | null;
  /** commission USD from those same accounts (subset of `commission`) */
  volumeUsd: number | null;
  /** how many of our accounts traded at all in the range */
  volumeAccounts: number | null;
  commissionDaysTotal: number;
  errors?: string[];
}

/** RoboForex je Microsoft-Kampagne (Relaunch 2026-10-01) aus dem Tracker: Klicks, zugeordnete Registrierungen,
 *  FTDs sowie Provision und Lots der zugeordneten Handelskonten. "ohne Klick" / "ChatGPT Ads" sammeln den Rest. */
export interface RoboforexKampagneRow {
  kampagne: string;
  land: string | null;
  klicks: number;
  regs: number;
  ftds: number;
  usd: number;
  lots: number;
}

/** Klicks, Registrierungen und FTDs je Microsoft-Kampagne fuer EINE Marke (Tracker /stats/kampagnen?broker=, Wien-Tage;
 *  Operator 2026-10-10: Detailtabelle der Sammelkonto-Marken wie 1xBet). Kampagne = cn= aus der Landing-URL des Klicks. */
export interface KampagneRow {
  kampagne: string;
  klicks: number;
  regs: number;
  ftds: number;
}

export interface KampagnenPayload {
  from: string;
  to: string;
  broker: string;
  generatedAt: string;
  rows: KampagneRow[];
}

/** 1xBet-Werte aus dem 1xPartners-Portal (Tracker /stats/onexbet, Abruf alle 30 Min; Operator 2026-10-10). Betraege in USD,
 *  Tage = Portal-Tage (UTC+3). gewinn = Firmengewinn (1xBet), provision = unsere RevShare-Provision. */
export interface OnexbetPayload {
  from: string;
  to: string;
  generatedAt: string;
  summe: { regs: number; ftd: number; einzahler: number; einzahlungen: number; gewinn: number; provision: number };
  tage: { tag: string; regs: number; ftd: number; einzahler: number; einzahlungen: number; gewinn: number; provision: number }[];
  /** Kennzahlen der Startseite: sumYesterday, sumCurrentMonth, sumAvailableWithdraw, sumCommissionHold … */
  konto: Record<string, number>;
  /** Zeitpunkt des letzten erfolgreichen Abrufs (ISO) */
  stand: string | null;
  /** Letzter Fehler, falls juenger als der letzte Erfolg */
  fehler: string | null;
}

export interface RoboforexKampagnenPayload {
  from: string;
  to: string;
  generatedAt: string;
  rows: RoboforexKampagneRow[];
  usd: number | null;
  lots: number | null;
  errors?: string[];
}

/** Google-Ads-Kampagne (Operator 2026-10-02: eToro ueber Google als eigener Bereich). Zahlen aus der Google Ads API,
 *  regs/ftds/cpa aus dem Tracker (Zuordnung ueber cid={campaignid} im Final-URL-Suffix). */
export interface GoogleCampaign {
  id: string;
  name: string;
  status: string;
  impressions: number;
  clicks: number;
  /** In Kontowaehrung (EUR) */
  spend: number;
  /** Conversions laut Google (hochgeladene Tracker-Conversions) */
  conversions: number;
  trackerClicks: number;
  regs: number;
  ftds: number;
  cpa: number;
  /** Anzeigengruppen (nur Google, Operator 2026-10-23: Kampagne "Kaufanleitungen DE/FR/NL" mit Laendern/Seiten als Gruppen):
   *  aktive Gruppen aktiver Kampagnen auch mit 0, sonst nur mit Daten im Zeitraum. Fehlt, wenn der Gruppenabruf scheiterte
   *  (nie als 0 lesen). */
  adgroups?: GoogleAdGroup[];
}

/** Anzeigengruppe einer Google-Kampagne (Tracker /stats/google/campaigns). Kosten/Klicks aus Google; trackerClicks/regs/ftds/cpa
 *  = Tracker, Zuordnung ueber agid={adgroupid} im Final-URL-Suffix (cpa = Trader). */
export interface GoogleAdGroup {
  adGroupId: string;
  name: string;
  status: string;
  impressions: number;
  clicks: number;
  spend: number;
  conversions: number;
  trackerClicks: number;
  regs: number;
  ftds: number;
  cpa: number;
}

/** Kapitaluebersicht (Operator 2026-10-02): Revolut-Kontostand + offene Provisionen je Auszahlungstermin. */
export interface KapitalAuszahlung {
  /** Provisionsmonat YYYY-MM */
  monat: string;
  /** Erwarteter Auszahlungstag (15. des Folgemonats), YYYY-MM-DD */
  auszahlung: string;
  etoro: KapitalPartner;
  avatrade: KapitalPartner;
  /** Noch erwartete Summe (ohne bereits eingegangene Partnerzahlungen). */
  summeUsd: number;
  /** Zahltag vorbei, Geld fehlt noch: auszahlung = heute, planTag = urspruenglicher 15. (Operator 2026-10-13). */
  ueberfaellig?: boolean;
  planTag?: string;
}

/** Provision eines Partners fuer einen Monat; eingegangen = Revolut-Eingang im Auszahlungsmonat (steckt im Kontostand). */
export interface KapitalPartner {
  cpa: number;
  usd: number;
  eingegangen?: { datum: string; betrag: number; waehrung: string };
}

/** Revolut-Transaktion (nur unbedenkliche Felder; keine Karten-/Namens-/Telefondaten). */
export interface KapitalTransaktion {
  zeit: string;
  typ: string;
  status: string;
  text: string;
  buchungen: { konto: string; betrag: number; waehrung: string; gebuehr: number; saldo: number | null }[];
}

export interface KapitalPayload {
  generatedAt: string;
  revolut: { verbunden: boolean; konten: { name: string; waehrung: string; saldo: number }[]; stand?: string; fehler?: string };
  auszahlungen: KapitalAuszahlung[];
  /** Letzte Revolut-Transaktionen (neueste zuerst). */
  transaktionen?: KapitalTransaktion[];
  /** Offene Schulden in EUR (Netto-Ansicht), aus der Tracker-.env KAPITAL_SCHULDEN_EUR. */
  schuldenEur?: number;
  regel?: string;
}

/** Meta-Kampagnen (Operator 2026-10-13, Karte "eToro · Facebook"): gleiches Format wie Google; Ausgaben in USD.
 *  conversions bleibt 0 - Metas "Leads" (eToro-Klick, darauf optimiert die Kampagne) zaehlen bewusst nicht, nur
 *  Tracker-Registrierungen (regs/ftds). */
export interface MetaCampaign extends GoogleCampaign {
  landingPageViews?: number;
  /** Anzeigengruppen (Meta ad sets, Operator 2026-10-22, wie die Microsoft-Anzeigengruppen): aktive Gruppen laufender
   *  Kampagnen auch mit 0, sonst nur mit Daten im Zeitraum. Fehlt, wenn der Gruppenabruf scheiterte (nie als 0 lesen). */
  adsets?: MetaAdSet[];
}

/** Anzeigengruppe einer Meta-Kampagne (Tracker /stats/meta/campaigns). clicks = Link-Klicks; trackerClicks/regs/ftds/cpa
 *  = Tracker, Zuordnung ueber utm_term={{adset.id}} in der Landing-URL (cpa = Trader). */
export interface MetaAdSet {
  adsetId: string;
  name: string;
  status: string;
  impressions: number;
  clicks: number;
  spend: number;
  landingPageViews: number;
  trackerClicks: number;
  regs: number;
  ftds: number;
  cpa: number;
}

/** Reddit-Kampagnen (Tracker /stats/reddit/campaigns, Operator 2026-10-15): gleiches Format wie Meta; landingPageViews =
 *  echte Seitenaufrufe (tracker.js-Ping), conversions bleibt 0 - es zaehlen nur Tracker-Registrierungen. */
export type RedditCampaignsPayload = MetaCampaignsPayload;

/** MGID-Kampagnen (Tracker /stats/mgid/campaigns, Operator 2026-10-16): gleiches Format wie Reddit (Kosten in EUR). */
export type MgidCampaignsPayload = MetaCampaignsPayload;

/** Taboola-Kampagnen (Tracker /stats/taboola/campaigns, Operator 2026-10-16): gleiches Format (Kosten in EUR). */
export type TaboolaCampaignsPayload = MetaCampaignsPayload;

export interface MetaCampaignsPayload {
  from: string;
  to: string;
  generatedAt: string;
  account: { id: string; name: string; currency: string };
  campaigns: MetaCampaign[];
}

export interface GoogleCampaignsPayload {
  from: string;
  to: string;
  generatedAt: string;
  account: { id: string; name: string; currency: string };
  campaigns: GoogleCampaign[];
}

export interface NewsTopic {
  id: string;
  label: string;
  query: string;
}

export interface NewsItem {
  id: string;
  topic: string;
  title: string;
  source: string | null;
  url: string;
  imageUrl: string | null;
  publishedAt: string;
  fetchedAt: string;
}

export interface NewsDigest {
  generatedAt: string;
  model: string;
  overall: string[];
  perTopic: Record<string, string[]>;
}

// ---- KI-Modelle: Rangliste nach Artificial Analysis Intelligence Index ----
// Quelle ist die Chart unter artificialanalysis.ai/#intelligence (dieselbe Modellauswahl),
// gelesen vom Ingest-Worker dreimal am Tag. Gezeigt werden die 20 besten Modelle samt
// Kosten; als Aenderung zaehlen nur andere Modelle oder eine andere Reihenfolge in den
// Top 10 - reine Punktverschiebungen und Bewegungen auf Platz 11-20 nicht.

/** Kosten (USD) je Aufgabe des Intelligence Index, aufgeteilt wie in der
 *  "Cost per Intelligence Index Task"-Chart. `input` = nicht gecachter Input. */
export interface AiTaskCost {
  total: number;
  answer: number;
  reasoning: number;
  cacheWrite: number;
  cacheHit: number;
  input: number;
}

export interface AiModelScore {
  slug: string;
  /** Kurzname wie in der Chart, z. B. "GPT-6 Astra (max)". */
  name: string;
  creator: string | null;
  score: number;
  cost: AiTaskCost | null;
}

export interface AiTop10Change {
  /** Zeitpunkt, an dem der neue Stand zum ersten Mal gelesen wurde (ISO). */
  at: string;
  indexVersion: string | null;
  prevIndexVersion: string | null;
  entered: { slug: string; name: string; rank: number; score: number }[];
  left: { slug: string; name: string; prevRank: number }[];
  /** Nur Modelle, die andere ueberholt haben bzw. ueberholt wurden - nicht jedes
   *  Modell, das durch einen Neuzugang einen Platz nach unten rutscht. */
  moved: { slug: string; name: string; from: number; to: number }[];
}

export interface AiRankingPayload {
  /** Letzter erfolgreicher Abruf; null = noch nie gelesen. */
  checkedAt: string | null;
  /** Seit wann die aktuelle Top-10-Reihenfolge gilt. */
  since: string | null;
  indexVersion: string | null;
  /** Die 20 besten Modelle, Rang 1 zuerst. */
  models: (AiModelScore & { rank: number })[];
  /** Aenderungen der Top 10, neueste zuerst, hoechstens 10. */
  changes: AiTop10Change[];
}

export const NEWS_TOPICS: NewsTopic[] = [
  {
    id: "ai-labs",
    label: "AI Labs",
    query: 'OpenAI OR Anthropic OR "Google DeepMind" OR Moonshot OR xAI',
  },
  {
    id: "ai-biz",
    label: "AI Industry & Investment",
    query: '"AI investment" OR "AI funding" OR "AI startup" OR Stargate OR Nvidia AI',
  },
  {
    id: "big-tech",
    label: "Big Tech",
    query: 'earnings (Apple OR Microsoft OR Google OR Meta OR Amazon OR NVIDIA OR Tesla)',
  },
  {
    id: "geo-iran",
    label: "Iran",
    query: 'Iran war OR Iran Israel OR Iran sanctions',
  },
  {
    id: "geo-ukr",
    label: "Ukraine",
    query: 'Ukraine Russia war OR Ukraine front OR Zelensky',
  },
  {
    id: "geo-tw",
    label: "China / Taiwan",
    // Broadened from 'China Taiwan OR Taiwan Strait OR PLA Taiwan': that narrow
    // escalation-only phrasing went stale for days between drill/statement news
    // cycles (GNews had nothing newer to return), so this topic lagged the others.
    // Require "Taiwan" + any geopolitics/defense context term to stay fresh daily.
    query: 'Taiwan (China OR Beijing OR "Taiwan Strait" OR PLA OR military OR defense OR sovereignty OR invasion)',
  },
];

// ---- OpenAI Ads (ChatGPT Ads) — Kampagnen je Ad-Account, geliefert vom Tracker ----
// (/stats/openai/campaigns; die Ads-API-Keys liegen nur dort). Beträge in der
// Account-Währung `currency` (beide Konten EUR), Tage in Account-Zeit (Wien).
export interface OpenaiCampaign {
  campaignId: string;
  campaignName: string;
  /** active | paused | archived | … (Kleinschreibung) */
  status: string;
  dailyBudget: number | null;
  impressions: number;
  clicks: number;
  spend: number;
  /** Von OpenAI attribuierte Conversions (Klick-Fenster, verzögert berichtet). */
  conversions: number;
  costPerConversion: number | null;
  /** Geschätzte Höchstkosten (Tracker, 2026-09-26): Klicks je Anzeigengruppe × deren aktuelles
   *  Höchstgebot, nie unter `spend`. OpenAI meldet die Kosten des laufenden Tages verzögert, die
   *  Klicks fast sofort. null = Gebote nicht lesbar; fehlt bei älteren Tracker-Ständen. */
  maxCost?: number | null;
  /** Höchstes aktuelles Gebot der Kampagne (je Klick, Account-Währung). */
  maxCpc?: number | null;
}

export interface OpenaiAccount {
  /** nordvpn | etoro — Schlüssel in OPENAI_ACCOUNTS des Trackers */
  broker: string;
  accountId: string;
  accountName: string;
  currency: string;
  /** OpenAI-Kontostatus (active | suspended …); gesperrte Konten blendet das Dashboard aus. */
  status?: string;
  campaigns: OpenaiCampaign[];
}

/** Tracker-eigene Conversions mit Klickquelle openai (unabhängig von OpenAIs Reporting). */
export interface OpenaiTrackerCount {
  broker: string;
  stage: string;
  count: number;
}

export interface OpenaiCampaignsPayload {
  from: string;
  to: string;
  generatedAt: string;
  accounts: OpenaiAccount[];
  tracker: OpenaiTrackerCount[];
  errors?: string[];
}

// ---- Conversions je Produkt — Tracker /stats/conversions (alle Broker, alle Quellen) ----
/** Eine Zeile je (Broker, Stufe, Land, Quelle) im Zeitraum (Wien-Tage, ohne Storno);
 *  value = Summe der gespeicherten Payouts (praktisch USD). Land/Quelle stammen vom
 *  verknüpften Klick, ohne Klick "unknown" — gleiche Labels wie EtoroBreakdownRow. */
export interface ConversionRow {
  broker: string;
  stage: string;
  geo: string;
  source: string;
  /** Gerät des Klicks (seit 2026-09-25): "desktop" | "ios" | "android" | "unknown".
   *  Optional, weil ältere Tracker-Antworten das Feld nicht kennen. */
  device?: string;
  /** Kampagne des Klicks (seit 2026-10-29): "cid:<Kampagnen-ID>" oder utm_campaign (Microsoft = Kampagnenname), sonst "".
   *  Damit zeigt das Dashboard, wenn eine Conversion aus einer Kampagne ausserhalb der Karte stammt. */
  campaign?: string;
  count: number;
  value: number;
}

export interface ConversionsPayload {
  from: string;
  to: string;
  generatedAt: string;
  rows: ConversionRow[];
  errors?: string[];
}

// ---- Landing-Kennzahlen je Kampagne — Tracker /stats/landings (Operator 2026-10-10) ----
/** Wie verhalten sich Anzeigen-Besucher auf der Landingpage? Eine Zeile je (Quelle, Kampagne) im Zeitraum
 *  (Wien-Tage wie die anderen /stats/*-Endpunkte). Feldnamen wie im Tracker-Vertrag (snake_case). */
export interface LandingRow {
  /** google | meta | microsoft */
  source: string;
  campaign_id: string;
  /** Kampagnenname; null -> campaign_id zeigen */
  campaign: string | null;
  /** Anfragen, die Cloudflare erreicht haben (inkl. Bots ohne JS) */
  edge: number;
  /** Seite im Browser geladen (JS-Ping) = echte Besuche */
  loaded: number;
  /** Aufteilung von loaded */
  desktop: number;
  mobile: number;
  tablet: number;
  /** Sekunden; null = keine Daten */
  avg_dwell_s: number | null;
  median_dwell_s: number | null;
  /** 0-100; null = keine Daten */
  avg_scroll_pct: number | null;
  /** Besuche mit mindestens einem Klick auf einen Partner-Knopf */
  cta_visits: number;
  /** cta_visits / loaded; null wenn loaded = 0 */
  cta_rate: number | null;
  /** Tracker-Klicks auf /go/etoro aus dieser Kampagne */
  etoro_clicks: number;
}

export interface LandingsPayload {
  from: string;
  to: string;
  rows: LandingRow[];
}
